import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['budgeting-from-take-home-pay-not-gross-fantasy'];

export default function BudgetingFromTakeHomePayPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['your-daily-freedom-number', 'tax-aware-fire-planning']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A household budget should start from take-home reality, not from an inflated gross-income story.',
          'Payroll deductions matter because they change what the household can truly spend and save.',
          'Per Diem treats wage income and budgeting as connected inputs into the same financial model.',
        ]}
      />

      <ArticleSection title="Gross income is a useful headline and a bad budget">
        <ArticleParagraph>
          Households often know their salary long before they know what that salary actually means
          in practice. Gross pay is simple to quote, but it is not the amount available for monthly
          life once payroll deductions, benefits, taxes, and retirement contributions have already
          claimed their share.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why many budgets feel unrealistic from the start. They are built around a number
          the household never really touches.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="A useful budget begins with the money the household actually has available, not with the version of income that exists only on paper."
        variant="phone"
      />

      <ArticleSection title="Take-home pay is where spending decisions actually happen">
        <ArticleParagraph>
          The question most households face is not “What is my salary?” It is “What is left after
          the commitments already baked into each paycheck?” That is the number that shapes grocery
          decisions, vacations, housing tradeoffs, and whether the current month still feels under
          control.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Payroll taxes change what actually lands in the bank account.',
            'Retirement contributions can be intentional savings, but they still reduce near-term cash flow.',
            'Insurance and other deductions affect what a household can spend before it touches discretionary categories.',
            'Ignoring those realities makes the budget look better than the lived experience.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Why this matters for FIRE planning too">
        <ArticleParagraph>
          FIRE households care about savings rate, but that does not mean the spending plan should
          ignore how money moves through payroll. A more accurate budget helps the household
          understand both how much it is saving and how much flexibility it truly has day to day.
        </ArticleParagraph>
        <ArticleParagraph>
          In Per Diem, that matters because the budget is not isolated. It feeds the daily freedom
          number and the long-term model. If the budget is built on fantasy, the guidance becomes
          fantasy too.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan"
        caption="A stronger FIRE plan starts with a stronger picture of current cash flow and household behavior."
        variant="phone"
      />

      <ArticleSection title="The point is a more believable operating picture">
        <ArticleParagraph>
          Budgeting from take-home pay is not about being pessimistic. It is about building a model
          the household can actually use without constantly translating between “financial plan” and
          “what my paycheck really looks like.”
        </ArticleParagraph>
        <ArticleParagraph>
          That is what makes the budget a planning tool instead of a guilty aspiration.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
